import { useContext, Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useBreakpoints from '../../../../../common/hooks/useBreakpoints';
import Carousel from '../../../../../common/widgets/Carousel';
import { ProductTypeSlugs, TopLevelCategoryBasePath } from '../../../constants';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { RevolutionSliderTypes, TopLevelCategories } from '../../../enums';
import { rewriteImageUrl } from '../../../helpers';
import { SlideshowItemModel } from '../../../models/RevolutionSlider';
import { isInternalLink } from '../../BenefitsPage/utils';
import { CarouselsContext } from '../../../contexts/CarouselsProvider';

interface MktSlideshowProps {
  sliderType: RevolutionSliderTypes;
  showDotsOnDesktop?: boolean;
}

const getDiscountVouchersLink = (item: SlideshowItemModel): string => {
  const path =
    item.goTo && Number(item.goTo) !== TopLevelCategories.DiscountVouchers
      ? `/discount-vouchers/${item.goTo}`
      : TopLevelCategoryBasePath[TopLevelCategories.DiscountVouchers];
  return item.brand ? `${path}?brand=${item.brand}` : path;
};

const mapLinkByType = (item: SlideshowItemModel): string => {
  switch (item.type) {
    case 0:
      return '';
    case 1:
      return item.goTo;
    case 2:
      return `/products/${item.goTo}`;
    case 3:
      return `/flexible-benefits/${item.goTo}${
        item.brand ? `?brand=${item.brand}` : ''
      }`;
    case 4:
      return `/club-benefits/${item.goTo}${
        item.brand ? `?brand=${item.brand}` : ''
      }`;
    case 5:
      return '/my-account/cards';
    case 6: {
      const slug = ProductTypeSlugs[Number(item.goTo)];
      if (!slug) {
        console.warn(`Unknown product type: ${item.goTo}`);
        return '';
      }
      return `/products/type/${slug}`;
    }
    case 7:
      return '/my-account/dashboard';
    case 8: {
      const path = item.goTo ? `/cashback/${item.goTo}` : '/cashback';
      return item.brand ? `${path}?brand=${item.brand}` : path;
    }
    case 9:
      return getDiscountVouchersLink(item);
    case 10:
      return '/my-account/my-benefits';
    default:
      // a slide type added in the CMS but not known here yet must not break
      // the whole page: show the slide without a link
      console.warn(`Unknown slide type: ${item.type}`);
      return '';
  }
};

export default function MktSlideshow(props: MktSlideshowProps) {
  const { showDotsOnDesktop = true } = props;
  const { isOnlyDesktop, isOnlyTablet, isOnlyMobile } = useBreakpoints();
  const { customerInfo } = useContext(CustomerInfoContext);
  const { slideshow, loading } = useContext(CarouselsContext);
  const [isInViewport, setIsInViewport] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const items = slideshow;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsInViewport(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const sendPaEvent = (
    eventName: string,
    currentIndex: number,
    currentItem: SlideshowItemModel,
  ) => {
    if (globalThis.pa) {
      let viewCounts = 0;
      if (eventName === 'hero_banner.click' && currentItem.type === 1) {
        const viewInfo = JSON.parse(
          localStorage.getItem('slide-view-info') ?? '{}',
        );
        viewCounts = viewInfo.link?.[currentItem.goTo ?? ''] ?? 0;
        if (viewInfo.link) viewInfo.link[currentItem.goTo ?? ''] = 0;
        localStorage.setItem('slide-view-info', JSON.stringify(viewInfo));
      }
      globalThis.pa.sendEvent(eventName, {
        location: (() => {
          switch (props.sliderType) {
            case RevolutionSliderTypes.HomeSlideshow:
              return 'homepage';
            case RevolutionSliderTypes.FlexibleBenefitsSlideshow:
              return 'benflex';
            case RevolutionSliderTypes.BenefitClubSlideshow:
              return 'benclub';
          }
        })(),
        position: currentIndex,
        type: currentItem.type,
        brand: currentItem.brand,
        goTo: currentItem.goTo,
        views: viewCounts,
        customer_group_id: customerInfo?.customerGroupId,
      });
    }
  };

  const renderImage = (
    currentIndex: number,
    currentItem: SlideshowItemModel,
  ) => {
    if (isOnlyDesktop) {
      return (
        <button
          type="button"
          onClick={() =>
            sendPaEvent('hero_banner.click', currentIndex, currentItem)
          }
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <img
            src={rewriteImageUrl(currentItem.imageDesktop)}
            alt="Dynamic title"
          />
        </button>
      );
    }
    if (isOnlyTablet || isOnlyMobile) {
      return (
        <button
          type="button"
          onClick={() =>
            sendPaEvent('hero_banner.click', currentIndex, currentItem)
          }
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <img
            src={rewriteImageUrl(currentItem.imageMobile)}
            alt="Dynamic title"
          />
        </button>
      );
    }
    return null;
  };

  const getSlideStorageEntry = (
    currentItem: SlideshowItemModel,
  ): { group: string; key: string } | null => {
    switch (currentItem.type) {
      case 1:
        return { group: 'link', key: currentItem.goTo };
      case 2:
        return { group: 'product', key: currentItem.goTo };
      case 3:
      case 4:
      case 9:
        return currentItem.brand
          ? {
              group: 'category_brand',
              key: `${currentItem.goTo}-${currentItem.brand}`,
            }
          : { group: 'category', key: currentItem.goTo };
      case 5:
        return { group: 'my-cards', key: currentItem.goTo };
      case 6:
        return { group: 'product_type', key: currentItem.goTo };
      default:
        return null;
    }
  };

  const saveSlideInfoToLocalStorage = (currentItem: SlideshowItemModel) => {
    const entry = getSlideStorageEntry(currentItem);
    if (!entry) return;

    const existing = JSON.parse(
      localStorage.getItem('slide-view-info') ?? '{}',
    );
    const updated = {
      ...existing,
      [entry.group]: {
        ...existing[entry.group],
        [entry.key]: {
          views: (existing[entry.group]?.[entry.key]?.views ?? 0) + 1,
          sd: existing[entry.group]?.[entry.key]?.sd ?? new Date(),
        },
      },
    };

    localStorage.setItem('slide-view-info', JSON.stringify(updated));
  };

  const hasMultipleSlides = !!items && items.length > 1;

  return (
    <div
      ref={containerRef}
      className={`mkt-slideshow edn-carousel edn-carousel-arrows no-arrows-tablet no-arrows-mobile${
        !showDotsOnDesktop ? ' no-dots-desktop' : ''
      }${hasMultipleSlides ? ' edn-carousel-dots' : ''}`}
    >
      <Carousel
        autoplay={true}
        autoplaySpeed={5000}
        dots={hasMultipleSlides && showDotsOnDesktop}
        infinite={hasMultipleSlides}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        arrows={hasMultipleSlides}
        onInit={() => {
          if (!items || !isInViewport || !items.length) return;
          const currentItem = items[0];
          sendPaEvent('hero_banner.display', 0, currentItem);
          saveSlideInfoToLocalStorage(currentItem);
        }}
        afterChange={(currentIndex: number) => {
          if (!items || !isInViewport) return;
          const currentItem = items[currentIndex];
          sendPaEvent('hero_banner.display', currentIndex, currentItem);
          saveSlideInfoToLocalStorage(currentItem);
        }}
        loading={loading.slideshow}
        responsive={[
          {
            breakpoint: 992,
            settings: {
              arrows: false,
              dots: hasMultipleSlides,
            },
          },
        ]}
      >
        {items?.map((e, currentIndex) => {
          const link = mapLinkByType(e);
          // the link alone is not unique: slides without a link all share ''
          // and two slides may point to the same page
          const key = `${currentIndex}-${link}`;
          switch (true) {
            case isInternalLink(link):
              return (
                <Link key={key} to={link} className="mkt-slide">
                  {renderImage(currentIndex, e)}
                </Link>
              );
            case link === '':
              return (
                <Fragment key={key}>{renderImage(currentIndex, e)}</Fragment>
              );
            default:
              return (
                <a
                  key={key}
                  href={link}
                  className="mkt-slide"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {renderImage(currentIndex, e)}
                </a>
              );
          }
        })}
      </Carousel>
    </div>
  );
}
